import {FC} from 'react';
import {Link as RouterLink} from 'react-router-dom';
import {Link} from '@mui/material';
import capitalize from 'lodash/capitalize';

import {
  ROUTE__DEPOSIT_FULL_INFO,
  ROUTE__ORDER_FULL_INFO,
  ROUTE__WITHDRAWAL_FULL_INFO,
} from '@shared/constants';
import {RelatedEntityType, Transaction} from '../types';

// The value is backend-specific (PaymentTypeEnum)
const PAYMENT_TYPE__DEPOSIT = 'deposit';

// Payments carry the ID in meta instead of `related_entity_id`,
// e.g. `DepositID 019fadb6-66ce-74a5-941e-b6185eb161da`
const getIdFromMeta = (meta: string[]): string | null => {
  for (const item of meta) {
    const match = /ID\s+(\S+)/.exec(item);

    if (match?.[1]) {
      return match[1];
    }
  }

  return null;
};

interface LinkTarget {
  route: string;
  id: string;
}

const getLinkTarget = ({
  relatedEntity,
  relatedEntityId,
  paymentType,
  meta,
}: Transaction): LinkTarget | null => {
  if (relatedEntity === RelatedEntityType.order && relatedEntityId) {
    return {route: ROUTE__ORDER_FULL_INFO, id: relatedEntityId};
  }

  if (relatedEntity === RelatedEntityType.payment) {
    const id = getIdFromMeta(meta) ?? relatedEntityId;

    if (id) {
      return {
        route:
          paymentType === PAYMENT_TYPE__DEPOSIT
            ? ROUTE__DEPOSIT_FULL_INFO
            : ROUTE__WITHDRAWAL_FULL_INFO,
        id,
      };
    }
  }

  return null;
};

interface Props {
  transaction: Transaction;
}

export const RelatedEntityLink: FC<Props> = ({transaction}) => {
  const {relatedEntity, userId} = transaction;

  if (!relatedEntity) {
    return null;
  }

  const text = capitalize(relatedEntity.replace(/_/g, ' '));
  const target = getLinkTarget(transaction);

  if (!target) {
    return <>{text}</>;
  }

  return (
    <Link
      component={RouterLink}
      to={target.route.replace(':userId', userId).replace(':id', target.id)}
      underline="hover"
    >
      {text}
    </Link>
  );
};
